export const OBJECT_TYPES = {
  additionalTaxInformation: 'additional_tax_information',
  advisingReport: 'advising_reports',
  agendaAppointmentDetails: 'appointment_details',
  agendaAppointments: 'appointments',
  agendaClientAccesses: 'client_accesses',
  agendaClientPhoneNumbers: 'client_phone_numbers',
  agendaContainers: 'containers',
  agendaFinancialAdvisorAvailabilities: 'availabilities',
  agendaFinancialAdvisorAvailabilityDetails: 'availability_details',
  agendaFinancialAdvisors: 'financial_advisors',
  agendaNewAppointments: 'new_appointments',
  agendaReasons: 'reasons',
  agendaTechnicalContexts: 'technical_contexts',
  aggregatedSeries: 'aggregated_series',
  bankConnection: 'bank_connections',
  beneficiaries: 'beneficiaries',
  beneficiaryClauseConfiguration: 'beneficiary_clause_configurations',
  budgetItem: 'budget_items',
  budgetItemConfiguration: 'budget_item_configurations',
  callToAction: 'call_to_actions',
  careers: 'careers',
  contract: 'contracts',
  contractLine: 'contract_lines',
  csp: 'csps',
  donation: 'donations',
  donationConfiguration: 'donation_configurations',
  donationDonee: 'donation_donees',
  donationGood: 'donation_goods',
  doneesConfiguration: 'donees_configuration',
  entryCategoryConfiguration: 'entry_category_configurations',
  esgData: 'esg_datas',
  esgQualificationScores: 'esg-qualification_scores',
  esgQualificationStepResponse: 'esg-qualification_step_responses',
  esgSurvey: 'esg_survey',
  estimate: 'estimate',
  estimateTimeseries: 'series',
  genericModel: 'genericModel',
  household: 'households',
  indicator: 'indicators',
  leg: 'legacies',
  legConfiguration: 'legacy_configurations',
  legReceiver: 'legacy_receivers',
  legGood: 'legacy_goods',
  attributorsConfiguration: 'attributors_configuration',
  receiversConfiguration: 'receivers_configuration',
  lines: 'lines',
  loans: 'loans',
  nniData: 'nni_data',
  ownership: 'ownerships',
  patrimonialIndicators: 'patrimonial_indicators',
  pedagogicalContents: 'pedagogical_contents',
  person: 'people',
  personConfiguration: 'person_configurations',
  personExternalData: 'person_external_data',
  piQualificationScores: 'pi-qualification_scores',
  piQualificationStepResponse: 'pi-qualification_step_responses',
  piSurvey: 'pi_survey',
  portfolio: 'portfolios',
  portfolioConfiguration: 'portfolio_configurations',
  previousStep: 'previous_step',
  productInvestments: 'product_investments',
  productGroupConfiguration: 'product_group_configurations',
  products: 'products',
  profession: 'professions',
  qceQualificationScores: 'qce-qualification_scores',
  qceQualificationStepResponse: 'qce-qualification_step_responses',
  qceSurvey: 'qce_survey',
  qualifications: 'qualifications',
  realEstateHolder: 'realEstateHolder',
  recommendation: 'recommendations',
  relation: 'relations',
  relationsMap: 'relations_maps',
  retirementFunds: 'retirement_funds',
  riskProfile: 'investor_profiles',
  serializableEstimationDatas: 'serializable_estimation_datas',
  studies: 'studies',
  studyHistory: 'study_histories',
  studyReport: 'study_reports',
  studyRetirementComplementaryInfo: 'study_retirement_complementary_infos',
  studyRetirementComplementaryInfos: 'study_retirement_complementary_infos',
  survey: 'survey',
  timeseries: 'portfolio_valuation_series',
  transaction: 'transactions',
  transactions: 'transactions',
  trimestersBuybacks: 'trimesters_buybacks',
} as const satisfies Record<string, string>

export const OBJECT_TYPES_KEYS = Object.keys(
  OBJECT_TYPES,
) as (keyof typeof OBJECT_TYPES)[]

export type ObjectTypeKeys = keyof typeof OBJECT_TYPES

export type ObjectType = ValueOf<typeof OBJECT_TYPES>

export const OWNERSHIP_CODES = {
  own: 'own',
  common: 'common',
} as const

export type OwnershipCode = ValueOf<typeof OWNERSHIP_CODES>

export const enum IS_NESTED {
  no = 'false',
  yes = 'true',
  ifNew = 'ifNew',
  never = 'never',
}

export const RELATIONSHIPS = {
  goalPortfolios: 'goal_portfolios',
}

export const INCLUDE_CLAUSES = {
  additionalTaxInformations: 'additional_tax_informations',
  beneficiaries: 'beneficiaries',
  budgetItem: 'budget_items',
  entryCategoryConfiguration: 'entry_category_configuration',
  esgQualification: 'esg_qualification',
  extendedPeople: 'extended_people',
  extendedPeopleProfessions: 'extended_people.professions',
  extendedRelations: 'extended_relations',
  lines: 'lines',
  linkableBudgetItems: 'linkable_budget_items',
  linkablePortfolios: 'linkable_portfolios',
  linkedBudgetItems: 'linked_budget_items',
  linkedPortfolios: 'linked_portfolios',
  nniData: 'nni_data',
  ownerships: 'ownerships',
  productCategoryConfiguration: 'product_category_configuration',
  productGroupConfiguration: 'product_group_configuration',
  productGroupMacroConfiguration: 'product_group_macro_configuration',
  profession: 'professions',
  recommendationReceived: 'recommendation_received',
  recommendationReceivedProducts:
    'recommendation_received.wealth_recommendation_products',
  recommendationReceivedProductsInvestments:
    'recommendation_received.wealth_recommendation_products.owned_product_investments',
  relation: 'relations',
  relationsMap: 'relations_map',
  rentEstimate: 'rent_estimate',
  rentEstimateSeries: 'rent_estimate_series',
  rentEstimateSeriesMax: 'rent_estimate_series_max',
  rentEstimateSeriesMin: 'rent_estimate_series_min',
  retirementComplementaryInfo: 'retirement_complementary_info',
  saleEstimate: 'sale_estimate',
  saleEstimateSeries: 'sale_estimate_series',
  saleEstimateSeriesMax: 'sale_estimate_series_max',
  saleEstimateSeriesMin: 'sale_estimate_series_min',
  storyHistoryWealthRecommendationProducts:
    'study_history.wealth_recommendation_products',
  storyHistoryWealthRecommendationProductsInvestments:
    'study_history.wealth_recommendation_products.owned_product_investments',
  studyHistory: 'study_history',
  studyReport: 'study_report',
  wealthRecommendationProducts: 'wealth_recommendation_products',
  wealthRecommendationProductsInvestments:
    'wealth_recommendation_products.owned_product_investments',
} as const

export const EQUIVALENT_OBJECT_TYPES = {
  [OBJECT_TYPES.personExternalData]: OBJECT_TYPES.person,
}
